import { defaultError } from '@qpr/client-library';
import api, { ApiRequest, ApiResult } from '../../../api/api-caller';
import initialState, { Data, DataTableState } from './datatable-state';

type APIData = {
  description: string;
  fullid: string;
  id: string;
  informationitems: string;
  link: string;
  name: string;
  processsteps: string;
  symbol: string;
  typename: string;
};

const fetchTableData = async (item: DataTableViewModelProps): Promise<ApiResult> => {
  const queryString = `@selectedElement = ${item.id};
  @subObjects = @selectedElement.subobjects;
  @documents = @subObjects.informationitems;
  @ = @subObjects;
  @ += @documents;`;

  const query: ApiRequest = {
    body: {
      query: queryString,
      attributes:
        'name, typename, symbol, fullid, description, infoitem.linklocation(as=link), functionasattribute(functionasattribute="processsteps", as="processsteps"), functionasattribute(functionasattribute="informationitems", as="informationitems")'
    },
    method: 'POST'
  };
  const result: ApiResult = await api.asyncCallApi('meaquery', query);

  return result;
};

const getTableData = (result: ApiResult): APIData[] => {
  const { data } = result;
  return data;
};

const errorHandler = (apiResult: ApiResult): DataTableState => {
  return {
    ...initialState,
    error: apiResult.error || defaultError
  };
};

const transformData = (dataset: APIData[], typeToSearch: string): Data[] => {
  const showOrphanRoles = false;

  const allRoles = dataset.filter((dataitem: APIData) => dataitem.typename === 'Organization Item');
  const allOfType = dataset.filter((dataitem: APIData) => dataitem.typename === typeToSearch);
  const allInfoItems = dataset.filter(
    (dataitem: APIData) => dataitem.typename === 'Information Item'
  );

  const finalData: Data[] = [];
  let linkedOfType: APIData[] = [];

  allRoles.forEach((role: APIData) => {
    if (role.processsteps.length > 0) {
      const foundForRole = allOfType.filter((x: APIData) => role.processsteps.includes(x.id));
      linkedOfType = linkedOfType.concat(foundForRole);
      if (foundForRole.length > 0) {
        foundForRole.forEach((found: APIData) => {
          if (found.informationitems.length > 0) {
            const informationItems = allInfoItems.filter((x: APIData) =>
              found.informationitems.includes(x.id)
            );

            informationItems.forEach((infoItem: APIData) =>
              finalData.push({
                role: role.name,
                tasks: found.name,
                informationitems: {
                  name: infoItem.name,
                  link: infoItem.link
                }
              })
            );
          } else {
            finalData.push({
              role: role.name,
              tasks: found.name
            });
          }
        });
      }
      if (showOrphanRoles && role.informationitems.length > 0) {
        const informationItems = allInfoItems.filter((x: APIData) =>
          role.informationitems.includes(x.id)
        );
        informationItems.forEach((infoItem: APIData) =>
          finalData.push({
            role: role.name,
            informationitems: {
              name: infoItem.name,
              link: infoItem.link
            }
          })
        );
      }
      if (showOrphanRoles && role.informationitems.length === 0 && foundForRole.length === 0) {
        finalData.push({
          role: role.name
        });
      }
    } else if (role.informationitems.length > 0) {
      const informationItems = allInfoItems.filter((x: APIData) =>
        role.informationitems.includes(x.id)
      );

      informationItems.forEach((infoItem: APIData) =>
        finalData.push({
          role: role.name,
          informationitems: {
            name: infoItem.name,
            link: infoItem.link
          }
        })
      );
    } else if (showOrphanRoles) {
      finalData.push({
        role: role.name
      });
    }
  });

  // add also items of chosen type (and their informationItems) that are not related to any roles
  const notLinkedOfType = allOfType.filter(
    (x: APIData) => !linkedOfType.map(la => la.id).includes(x.id)
  );

  notLinkedOfType.forEach((item: APIData) => {
    if (item.informationitems.length > 0) {
      const informationItems = allInfoItems.filter((x: APIData) =>
        item.informationitems.includes(x.id)
      );
      informationItems.forEach((infoItem: APIData) =>
        finalData.push({
          tasks: item.name,
          informationitems: {
            name: infoItem.name,
            link: infoItem.symbol
          }
        })
      );
    } else {
      finalData.push({
        tasks: item.name
      });
    }
  });

  return finalData;
};

const doGetData = async (item: DataTableViewModelProps): Promise<DataTableState> => {
  try {
    const result = await fetchTableData(item);

    if (!result.ok) {
      return errorHandler(result);
    }

    const tableData: APIData[] = getTableData(result);

    const transformedData = transformData(tableData, item.taskType);

    return {
      ...initialState,
      data: transformedData
    };
  } catch (error) {
    return {
      ...initialState,
      error: { message: 'Server unreachable', details: JSON.stringify(error) }
    };
  }
};

// allow use string to access element
interface IData {
  [key: string]: any;
}
const viewModelCache: IData = {};

export interface DataTableViewModelType {
  fetchData(): Promise<DataTableState>;
  getData(): Promise<DataTableState>;
  getId(): string;
}

interface DataTableViewModelProps {
  id: string;
  taskType: string;
}

const createDataTableViewModel = (item: DataTableViewModelProps): DataTableViewModelType => {
  let data: DataTableState;
  const cacheKey: string = JSON.stringify(item);

  const getId = (): string => item.id;
  const fetchData = async (): Promise<DataTableState> => {
    if (viewModelCache[cacheKey]) {
      data = viewModelCache[cacheKey];
    } else {
      data = await doGetData(item);
      viewModelCache[cacheKey] = data;
    }
    return data;
  };

  const getData = async (): Promise<DataTableState> => {
    if (data) {
      return data;
    }
    await fetchData();
    return data;
  };

  return {
    getId,
    fetchData,
    getData
  };
};

export default createDataTableViewModel;
